import { Link } from 'react-router-dom'
import { useI18n } from '../lib/i18n'

interface StatusRow {
  key: string
  state: 'ok' | 'wip' | 'no'
}

const statusRows: StatusRow[] = [
  { key: 'cuelist', state: 'ok' },
  { key: 'timecode', state: 'ok' },
  { key: 'cuelights', state: 'ok' },
  { key: 'ai', state: 'ok' },
  { key: 'tests', state: 'ok' },
  { key: 'dmg', state: 'ok' },
  { key: 'signing', state: 'wip' },
  { key: 'ltchw', state: 'wip' },
]

const todayItems = ['t1', 't2', 't3', 't4', 't5', 't6'] as const
const notItems = ['signing', 'ltchw', 'keys', 'public', 'repo'] as const
const bundleCards = ['b1', 'b2', 'b3', 'b4'] as const
const roadmapKeys = ['t1', 't2', 't3', 't4', 't5', 't6'] as const

/** Numbered section header — ŠVÝCAR v2 pattern (ghost Anton numeral + title + label). */
function SecH({ no, title, label }: { no: string; title: string; label: string }) {
  return (
    <div className="flex items-baseline gap-4 border-t border-rule pt-8 pb-6">
      <span className="ghost-no text-[44px] leading-none shrink-0">{no}</span>
      <h2 className="font-display font-extrabold text-2xl tracking-tight">{title}</h2>
      <small className="ml-auto section-label hidden sm:block">{label}</small>
    </div>
  )
}

/** Strip leading "0X — " from i18n section labels (number is rendered as ghost numeral). */
const stripNo = (s: string) => s.replace(/^\d+\s*—\s*/, '')

export function Home() {
  const { t, lang } = useI18n()
  const cs = lang === 'cs'

  const previewMailto =
    "mailto:hello@xlabproject.net?subject=ShowX%20dev%20preview%20cohort&body=Hi%20XLAB%2C%0A%0AI'd%20like%20a%20seat%20in%20the%20ShowX%20dev%20preview%20cohort.%0A%0AWho%20I%20am%3A%20%0AVenue%20%2F%20role%3A%20%0AWhat%20I%20want%20to%20see%20from%20ShowX%3A%20%0A%0AThanks!"

  return (
    <div>
      {/* HERO — low, factual */}
      <section className="border-b border-rule">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-14 lg:pt-16 pb-12 lg:pb-14 border-l border-r border-rule grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-end">
          <div>
            <div className="section-label mb-6">{t('home.label')}</div>
            <h1 className="font-display font-extrabold text-4xl lg:text-[56px] leading-[1.05] tracking-[-1px]">
              {t('home.hero.line1')}
              <br />
              {t('home.hero.line2')}
              <br />
              <span className="text-muted">{t('home.hero.line3')}</span>
            </h1>
            <p className="mt-5 text-lg text-muted max-w-[52ch] leading-relaxed">{t('home.hero.copy')}</p>
          </div>
          <div className="flex flex-col items-start gap-3">
            <Link to="/try-it" className="btn-primary">
              {t('home.cta.try')}
            </Link>
            <Link to="/status" className="btn-ghost">
              {t('home.cta.status')}
            </Link>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted mt-1">
              {t('home.live.footnote')}
            </span>
          </div>
        </div>
      </section>

      <main className="max-w-[1240px] mx-auto px-6 lg:px-10">
        {/* 01 — WHAT'S BUILT (live status grid) */}
        <section className="pt-2">
          <SecH no="01" title={t('home.live.status')} label="v0.7.0" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t border-rule mb-14">
            {statusRows.map(row => {
              const dotClass =
                row.state === 'ok'
                  ? 'bg-accent'
                  : row.state === 'wip'
                    ? 'bg-transparent border border-accent'
                    : 'bg-transparent border border-muted/40'
              const label =
                row.state === 'ok' ? t('home.live.ok') : row.state === 'wip' ? t('home.live.wip') : t('home.live.no')
              return (
                <div key={row.key} className="border-r border-b border-rule p-5 flex items-center justify-between gap-3 hover:bg-paper transition-colors">
                  <span className="font-mono text-[13px]">{t(`home.live.${row.key}`)}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className={`w-2 h-2 ${dotClass}`} />
                    <span className="font-mono text-[11px] text-muted">{label}</span>
                  </span>
                </div>
              )
            })}
          </div>
        </section>

        {/* 02 — WHAT'S LIVE (feature grid) */}
        <section>
          <SecH no="02" title={`${t('home.today.h1')} ${t('home.today.h2')}`} label={stripNo(t('home.today.label'))} />
          <p className="copy mb-8 max-w-2xl">{t('home.today.intro')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-rule">
            {todayItems.map(k => (
              <div key={k} className="border-r border-b border-rule p-7 hover:bg-paper transition-colors">
                <span className="block w-2.5 h-2.5 bg-accent mb-4" />
                <b className="block font-bold text-base mb-2">{t(`home.today.${k}`)}</b>
                <span className="text-muted text-sm leading-relaxed">{t(`home.today.${k}.body`)}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 mb-14">
            <Link
              to="/try-it"
              className="font-sans font-semibold text-xs uppercase tracking-[0.15em] text-accent hover:text-ink transition-colors"
            >
              {cs ? 'Plný návod k instalaci →' : 'Full install guide →'}
            </Link>
          </div>
        </section>

        {/* 03 — STILL PENDING */}
        <section>
          <SecH no="03" title={`${t('home.not.h1')} ${t('home.not.h2')}`} label={stripNo(t('home.not.label'))} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-rule mb-14">
            {notItems.map((k, i) => (
              <div key={k} className="border-r border-b border-rule p-7 hover:bg-paper transition-colors">
                <span className="ghost-no-accent block text-[56px] leading-none mb-3">{i + 1}</span>
                <b className="block font-bold text-base mb-2">{t(`home.not.${k}`)}</b>
                <span className="text-muted text-sm leading-relaxed">{t(`home.not.${k}.body`)}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 04 — GET A SEAT */}
        <section>
          <SecH no="04" title={`${t('home.seat.h1')} ${t('home.seat.h2')}`} label={stripNo(t('home.seat.label'))} />
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-10 items-end mb-14">
            <p className="copy max-w-xl">{t('home.seat.body')}</p>
            <div className="flex flex-col items-start gap-4">
              <a href={previewMailto} className="btn-primary">
                {t('home.seat.cta')} →
              </a>
              <div className="font-mono text-[11px] text-muted">
                hello@xlabproject.net · subject: ShowX dev preview cohort
              </div>
            </div>
          </div>
        </section>

        {/* 05 — BUILT (four layers) */}
        <section>
          <SecH no="05" title={`${t('home.bundle.h1')} ${t('home.bundle.h2')}`} label={stripNo(t('home.bundle.label'))} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-rule">
            {bundleCards.map((b, i) => (
              <div key={b} className="border-r border-b border-rule p-7 hover:bg-paper transition-colors">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="font-mono text-xs text-accent">{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                    {t(`home.bundle.${b}.status`)}
                  </span>
                </div>
                <b className="block font-bold text-base mb-2">{t(`home.bundle.${b}.title`)}</b>
                <span className="text-muted text-xs leading-relaxed">{t(`home.bundle.${b}.body`)}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 mb-14">
            <Link
              to="/status"
              className="font-sans font-semibold text-xs uppercase tracking-[0.15em] text-accent hover:text-ink transition-colors"
            >
              {cs ? 'Detailní status →' : 'Full status →'}
            </Link>
          </div>
        </section>

        {/* 06 — WHAT'S LEFT (roadmap strip) */}
        <section>
          <SecH no="06" title={`${t('home.roadmap.h1')} ${t('home.roadmap.h2')}`} label={stripNo(t('home.roadmap.label'))} />
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 border-l border-t border-rule mb-14">
            {roadmapKeys.map((k, i) => (
              <div key={k} className="border-r border-b border-rule p-5 hover:bg-paper transition-colors">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="font-mono text-xs text-accent">{String(i + 1).padStart(2, '0')}</span>
                  {i < roadmapKeys.length - 1 && <span className="text-muted text-xs">→</span>}
                </div>
                <b className="block font-bold text-sm mb-1">{t(`home.roadmap.${k}`)}</b>
                <span className="text-muted text-xs leading-relaxed">{t(`home.roadmap.${k}.body`)}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 07 — NEXT STEPS */}
        <section>
          <SecH no="07" title={`${t('home.foot.h1')} ${t('home.foot.h2')}`} label={stripNo(t('home.foot.label'))} />
          <div className="flex flex-wrap gap-3 mb-20">
            <Link to="/try-it" className="btn-primary">
              {t('home.foot.try')}
            </Link>
            <Link to="/docs" className="btn-ghost">
              {t('home.foot.docs')}
            </Link>
          </div>
        </section>
      </main>
    </div>
  )
}
